import type {
  HarnessEvent,
  HarnessAssistantEvent,
  HarnessResultEvent,
  HarnessSystemInitEvent,
  HarnessCompactBoundaryEvent,
  HarnessTaskStartedEvent,
  HarnessTaskProgressEvent,
  HarnessRateLimitEvent,
} from "../harness/index.js";
import type { TaskContext, ActivityEventSummary } from "@project/shared";
import type { QueryHost } from "./query-executor.js";
import type { UsageInfo } from "./event-handlers.js";
import {
  API_ERROR_PATTERN,
  handleAssistantCase,
  handleResultCase,
  handleRateLimitEvent,
  handleSystemEvent,
  handleSystemSubevents,
  handleToolProgressEvent,
} from "./event-handlers.js";

/** Mutable state bag threaded through the event loop. */
interface EventLoopState {
  sessionIdStored: boolean;
  isTyping: boolean;
  retriable: boolean;
  sawApiError: boolean;
  resultSummary: string | undefined;
  rateLimitResetsAt: string | undefined;
  staleSession: boolean | undefined;
  authError: boolean | undefined;
  lastAssistantUsage: UsageInfo | undefined;
  turnToolCalls: ActivityEventSummary[];
}

function stopTypingIfNeeded(host: QueryHost, isTyping: boolean): void {
  if (isTyping) host.connection.sendTypingStop();
}

/** Merge pending tool outputs into accumulated tool call summaries and emit turn_end. */
function flushPendingToolCalls(host: QueryHost, turnToolCalls: ActivityEventSummary[]): void {
  if (turnToolCalls.length === 0) {
    // Clear any orphaned outputs that arrived without matching tool_use blocks
    host.pendingToolOutputs.length = 0;
    return;
  }
  // Match outputs to tool calls by tool name (handles duplicate names via queue)
  const outputsByTool = new Map<string, string[]>();
  for (const entry of host.pendingToolOutputs) {
    const list = outputsByTool.get(entry.tool) ?? [];
    list.push(entry.output);
    outputsByTool.set(entry.tool, list);
  }
  for (const call of turnToolCalls) {
    const list = outputsByTool.get(call.tool);
    if (list && list.length > 0) {
      call.output = list.shift();
    }
  }
  host.connection.sendEvent({ type: "turn_end", toolCalls: [...turnToolCalls] });
  turnToolCalls.length = 0;
  host.pendingToolOutputs.length = 0;
}

async function processSystemCase(
  event:
    | HarnessSystemInitEvent
    | HarnessCompactBoundaryEvent
    | HarnessTaskStartedEvent
    | HarnessTaskProgressEvent,
  host: QueryHost,
  context: TaskContext,
  state: EventLoopState,
): Promise<void> {
  if (event.subtype === "init") {
    const stored = await handleSystemEvent(event, host, context, state.sessionIdStored);
    if (stored) state.sessionIdStored = true;
  } else {
    handleSystemSubevents(
      event as HarnessCompactBoundaryEvent | HarnessTaskStartedEvent | HarnessTaskProgressEvent,
      host,
    );
  }
}

async function processAssistantCase(
  event: HarnessAssistantEvent,
  host: QueryHost,
  state: EventLoopState,
): Promise<void> {
  if (!state.isTyping) {
    setTimeout(() => host.connection.sendTypingStart(), 200);
    state.isTyping = true;
  }
  const usage = await handleAssistantCase(event, host, state.turnToolCalls);
  if (usage) state.lastAssistantUsage = usage;

  // Check for API error patterns in assistant message text.
  // The SDK may emit API errors as assistant message content before ending
  // with a clean success result, which would bypass the result-level retry check.
  if (!state.sawApiError) {
    const fullText = event.message.content
      .filter((b: { type: string }) => b.type === "text")
      .map((b: { type: string; text?: string }) => (b as { text: string }).text)
      .join(" ");
    if (API_ERROR_PATTERN.test(fullText)) {
      state.sawApiError = true;
    }
  }
}

async function processResultCase(
  event: HarnessResultEvent,
  host: QueryHost,
  context: TaskContext,
  startTime: number,
  state: EventLoopState,
): Promise<void> {
  const info = await handleResultCase(
    event,
    host,
    context,
    startTime,
    state.isTyping,
    state.lastAssistantUsage,
  );
  if (info.stoppedTyping) state.isTyping = false;
  state.retriable = info.retriable;
  // If the result itself is clean (not retriable), any API error the agent
  // mentioned in assistant text was already recovered from — don't retry.
  if (!info.retriable) state.sawApiError = false;
  state.resultSummary = info.resultSummary;
  if (info.staleSession) state.staleSession = true;
  if (info.authError) state.authError = true;
}

export async function processEvents(
  events: AsyncGenerator<HarnessEvent, void>,
  context: TaskContext,
  host: QueryHost,
): Promise<{
  retriable: boolean;
  resultSummary?: string;
  modeRestart?: boolean;
  rateLimitResetsAt?: string;
  staleSession?: boolean;
  authError?: boolean;
}> {
  const startTime = Date.now();
  let lastStatusEmit = Date.now();
  const STATUS_REEMIT_INTERVAL_MS = 5_000;

  const state: EventLoopState = {
    sessionIdStored: false,
    isTyping: false,
    retriable: false,
    sawApiError: false,
    resultSummary: undefined,
    rateLimitResetsAt: undefined,
    staleSession: undefined,
    authError: undefined,
    lastAssistantUsage: undefined,
    turnToolCalls: [],
  };

  for await (const event of events) {
    if (host.isStopped()) break;

    // Flush any pending tool calls from the previous assistant turn
    // (tool outputs have been collected by PostToolUse hooks between events)
    flushPendingToolCalls(host, state.turnToolCalls);

    // Re-emit "running" periodically so missed status events self-correct
    const now = Date.now();
    if (now - lastStatusEmit >= STATUS_REEMIT_INTERVAL_MS) {
      host.connection.emitStatus("running");
      lastStatusEmit = now;
    }

    if (host.pendingModeRestart) {
      stopTypingIfNeeded(host, state.isTyping);
      return { retriable: false, modeRestart: true };
    }

    switch (event.type) {
      case "system":
        await processSystemCase(event as HarnessSystemInitEvent, host, context, state);
        break;
      case "assistant":
        await processAssistantCase(event as HarnessAssistantEvent, host, state);
        break;
      case "result":
        await processResultCase(event as HarnessResultEvent, host, context, startTime, state);
        break;
      case "rate_limit_event": {
        const resetsAt = handleRateLimitEvent(event as HarnessRateLimitEvent, host);
        if (resetsAt) state.rateLimitResetsAt = resetsAt;
        break;
      }
      case "tool_progress":
        handleToolProgressEvent(event, host);
        break;
    }
  }

  flushPendingToolCalls(host, state.turnToolCalls);
  stopTypingIfNeeded(host, state.isTyping);

  return {
    retriable: state.retriable || state.sawApiError,
    resultSummary: state.resultSummary,
    rateLimitResetsAt: state.rateLimitResetsAt,
    ...(state.staleSession && { staleSession: state.staleSession }),
    ...(state.authError && { authError: state.authError }),
  };
}
